import { eventsActions } from './events.era';
import { createActionGroup, createFeature, createReducer, emptyProps, on } from '@ngrx/store';
import { EntityState, createEntityAdapter } from '@ngrx/entity';
import { createFeatureSelector } from '@ngrx/store';
import { ErrorStreamChange } from '../models/$error.model';
import { date2str, str2Date } from 'globals/helpers/helpers';
import { Task, TaskTriggers, DBTaskPropertyWhiteList, Subtask } from 'globals/store/tasks.data';
import { userActions } from './user.era';
export { Task, TaskTriggers, DBTaskPropertyWhiteList, Subtask }

// Definitions
const CREATE   = '[Task] create';
const UPDATE   = '[Task] update triggers';
const DELETE   = '[Task] delete';
const ADD_TRIGGERS = '[Task] add task triggers'
const CLEAR_TRIGGERS = '[Task] clear task triggers'
const TASK_FOCUSED = '[Task] has focus';
// ...for firebase
const ADD_ONE  = '[Task] one added from db';
const MODIFIED = '[Task] modified from db';
const REMOVED  = '[Task] removed from db';
export const ADD_MANY_TASKS = '[Task] many added from db'
const DECREMENT_LOADING_COUNT = '[Task] decrement loading count'
const INCREASE_LOADING_COUNT = '[Task] increase loading count'
const UPDATE_CALENDAR_DATE = '[Task] update calendar date'

export const getTaskTriggers = ():TaskTriggers=>{
  return {
    task_status_has_changed: false,
    should_update_remindTime: false
  }
}

// Entity adapter
export const adapter = createEntityAdapter<Task>();
export interface TasksState extends EntityState<Task> {
  loadingCount: number
  calendarDate: Date
  triggers:{[taskId:string]:TaskTriggers}
  focused: string;
}

// Default data / initial state <== should be a function because it is possible otherwise some of the property objects would be shared across multiple new events
const getDefaultTasks = ():TasksState => ({
    ids: [],
    entities: {},
    loadingCount: 0,
    calendarDate: new Date(),
    triggers:{},
    focused:null
})
export const initialStateCreator = (): TasksState => adapter.getInitialState(getDefaultTasks());

// Actions
interface QueryParams {
  eventId:string,
  uid:string
}

export const tasksActions = createActionGroup({
  source: 'Tasks',
  events: {
    'Create Task': (task: Task, log_data: Partial<ErrorStreamChange>) => ({ task, log_data }),
    'Update Task': (id: string, changes: Partial<Task>, log_data: Partial<ErrorStreamChange>) => ({ id, changes, log_data }),
    'Delete Task': (id: string, log_data: Partial<ErrorStreamChange>) => ({ id, log_data }),
    'Add One Task': (task: Task) => ({ task }),
    'Add Many Tasks': (payload: Task[]) => ({ payload }),
    'Task In Focus': (taskId?:string) => ({ taskId }),
    'Add Triggers': (taskId:string, triggers:TaskTriggers) => ({ taskId, triggers }),
    'Clear Triggers': (taskId:string) => ({ taskId }),
    'Modified from DB': (id: string, changes: Partial<Task>) => ({ id, changes }),
    'Removed from DB': (id: string) => ({ id }),
    'Update Calendar Date': (payload: Date) => ({ payload }),
    'Decrease Loading Count': emptyProps(),
    'Increase Loading Count': (payload: number) => ({ payload })
  }
})

const hasTriggers = (triggers:TaskTriggers) => triggers.should_update_remindTime || triggers.task_status_has_changed || triggers.inform_creator_on_delete || triggers.was_responsible_on_delete || triggers.will_be_responsible || triggers.wont_be_responsible;

const taskCleaner = (task: Task): Task => { // mutates task in place
  if (task.dueDate && typeof task.dueDate !== "string" && task.dueDate.seconds) {
    task.dueDate = date2str(new Date(task.dueDate.seconds * 1000))
  } else if (task.dueDate) {
    task.date = str2Date(task.dueDate) // overwrite with needed value if present
  } else if (task.date) {
    delete task.date // else delete
  }
  if (task.seen && Array.isArray(task.seen)) { // cleanup for incorrectly setting empty seen to a list instead of an object
    task.seen = {}
  }
  return task
}

export const taskReducer = createReducer(
  initialStateCreator(),
  on(tasksActions.createTask, (state, { task }) => adapter.addOne(taskCleaner(task), {...state, loading:true })),
  on(tasksActions.updateTask, (state, { id, changes }) => adapter.updateOne({ id, changes}, {...state, loading:id })),
  on(tasksActions.deleteTask, (state, { id }) => adapter.removeOne(id, {...state, loading:id })),
  on(tasksActions.addOneTask, (state, { task }) => adapter.addOne(taskCleaner(task), {...state, loading:false })),
  on(tasksActions.addManyTasks, (state, { payload }) => {
    payload.forEach(taskCleaner)
    return adapter.addMany(payload, {...state })
  }),
  on(tasksActions.modifiedFromDB, (state, { id, changes }) => state.entities[id] ? // sometimes the task gets deleted because it is removed from one subscription
    adapter.updateOne({ id, changes}, {...state }) :
    adapter.addOne(taskCleaner(changes as Task), {...state })),
  on(tasksActions.removedFromDB, (state, { id }) => adapter.removeOne(id, {...state })),
  on(tasksActions.taskInFocus, (state, { taskId }) => ({ ...state, focused:taskId })),
  on(tasksActions.addTriggers, (state, { taskId, triggers }) => {
    if(!hasTriggers(triggers)) return state;
    const add_triggers = {...state.triggers}
    add_triggers[taskId] = triggers;
    return {...state, triggers:add_triggers}
  }),
  on(tasksActions.clearTriggers, (state, { taskId }) => {
    const triggers = {...state.triggers}
    delete triggers[taskId]
    return {...state, triggers }
  }),
  on(tasksActions.updateCalendarDate, (state, { payload }) => ({ ...state, calendarDate: payload })),
  on(tasksActions.decreaseLoadingCount, (state) => ({ ...state, loadingCount: Math.max(0, state.loadingCount - 1) })),
  on(tasksActions.increaseLoadingCount, (state, { payload }) => ({ ...state, loadingCount: state.loadingCount + payload })),
  on(userActions.logout, (state) => initialStateCreator()),
  // on(eventsActions.unloadEvent, (state) => initialStateCreator()),
  // on(eventsActions.startLoadingSingleEvent, (state) => initialStateCreator())
)

export const tasksFeature = createFeature({
  name: 'tasks',
  reducer: taskReducer,
});

export const tasksStateSelector = createFeatureSelector<TasksState>(tasksFeature.name)
